import { Injectable } from '@angular/core';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { User } from '../models/user';

import { AppSettings } from '../app.settings';
import { isAdoptedSession } from './auth-token';

@Injectable({
  providedIn: 'root'
})
export class SmsApiSessionService {

  constructor(private http: HttpClient) { }

  /**
   * Resolves the currently authenticated user, or rejects with the reason.
   *
   * Two token issuers are in play. A normal login is minted by the ecommerce API
   * (HS512, `sub` claim) and is validated by ecommerce-api/sms-session. A session
   * created through the admin ADOPT IDENTITY screen is minted by the legacy PHP
   * API (HS256, `id` claim); the ecommerce API cannot verify it - different
   * secret, different algorithm, no `sub` - so an adopted session is validated
   * against the legacy session endpoint, which is what issued the token.
   *
   * The legacy API's Auth_user library reads both claim shapes, so an adopted
   * token works against every legacy endpoint. Features backed by the ecommerce
   * API (sign-ups, the account statement view) will report as logged out while a
   * session is adopted; that is expected and is why the banner is there.
   */
  getSession(): Promise<User> {
    return isAdoptedSession() ? this.getLegacySession() : this.getEcommerceSession();
  }

  private getEcommerceSession(): Promise<User> {
    return new Promise((resolve, reject) => {
      this.http.get(AppSettings.NEW_API_ENDPOINT + 'sms-session').subscribe((response: any) => {

        if (response.data && response.data.type == 'session') {
          const attributes = response.data.attributes || {};

          // sms-session answers 200 with allowAccess/userLoggedIn false when the
          // token is missing, expired or unverifiable. This used to resolve with
          // attributes.user regardless - undefined - and AuthGuard then threw a
          // TypeError on user.enabled, so every auth failure looked identical and
          // the real reason never surfaced.
          if (!attributes.user || attributes.allowAccess === false || attributes.userLoggedIn === false) {
            reject(attributes.message || 'Your session is no longer valid. Please sign in again.');
            return;
          }

          resolve(attributes.user);

        } else if (response.type == 'auth_object' && response.data.is_logged_in) {
          resolve(new User({is_enabled: true}));
        } else {
          reject(response);
        }
      },
        error_response => {
          if (error_response instanceof HttpErrorResponse) {
            reject(error_response.error);
            return;
          }
          reject(error_response);
        });
    });
  }

  private getLegacySession(): Promise<User> {
    return new Promise((resolve, reject) => {
      this.http.get(AppSettings.API_ENDPOINT + 'sms_session').subscribe((response: any) => {

        // Sms_session.php returns the user through set_response(), which the REST
        // formatter wraps as {type: 'user', data: {...}}. Accept a bare user
        // object too, so a formatter change cannot silently log admins out.
        const user = (response && response.data && response.type == 'user')
          ? response.data
          : ((response && (response.id || response.username)) ? response : null);

        if (user) {
          resolve(user);
          return;
        }

        reject(response);
      },
        error_response => {
          if (error_response instanceof HttpErrorResponse) {
            // The legacy endpoint answers 403 with a plain string for an absent,
            // rejected or disabled token.
            reject(error_response.error || 'Your adopted session is no longer valid. Please adopt the identity again.');
            return;
          }
          reject(error_response);
        });
    });
  }

}
